import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CurrencyRupeeRoundedIcon from '@mui/icons-material/CurrencyRupeeRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import SyncDisabledRoundedIcon from '@mui/icons-material/SyncDisabledRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import {
  PitchTitle,
  ProChip,
  Shell,
} from 'src/components/careerProPitch/sheet.styles';
import { UserRole } from 'src/constants';
import isDoNotDisturbPath from 'src/constants/doNotDisturb';
import useRazorpay from 'src/helpers/hooks/useRazorpay';
import { isAdminMode } from 'src/helpers/storedToken';
import { formatMandateDate } from 'src/helpers/subscription';
import type { SubscriptionCancelNoticeSub } from 'src/openapi';
import { useAppDispatch, useAppSelector } from 'src/redux';
import {
  confirmSubscriptionCancel,
  getMySubscription,
} from 'src/redux/actions/store';
import { useSubscriptionRestore } from './useSubscriptionRestore';
import {
  ActionsRow,
  AnswerButton,
  BodyText,
  CoinBadge,
  Content,
  FactIcon,
  FactRow,
  FactText,
  FootNote,
  GoldCoin,
  Header,
  NoticeDialog,
  Question,
  RestoreCard,
  RestoreCardTitle,
} from './index.styles';

type Fact = { key: string; icon: ReactNode; text: ReactNode };

const noticeCopy = (
  notice: SubscriptionCancelNoticeSub,
): { title: string; body: string; facts: Fact[] } => {
  const date = formatMandateDate(notice.resume_at);
  const amount = notice.plan_amount != null ? `₹${notice.plan_amount}` : '';
  const payment = amount ? <strong>{amount}</strong> : 'payment';
  if (notice.kind === 'trial') {
    return {
      title: 'Your Pro subscription mandate has been cancelled',
      body: 'Your free trial benefits have stopped for now. Reauthorise now to continue your free trial benefits from where they stopped.',
      facts: [
        {
          key: 'trial',
          icon: <EventAvailableRoundedIcon />,
          text: (
            <>
              Your free trial carries on from where it stopped, until{' '}
              <strong>{date}</strong>
            </>
          ),
        },
        {
          key: 'payment',
          icon: <CurrencyRupeeRoundedIcon />,
          text: (
            <>
              First {payment} on <strong>{date}</strong>
            </>
          ),
        },
      ],
    };
  }
  return {
    title: 'Your Pro subscription mandate has been cancelled',
    body: `Your Pro benefits continue until ${date}. Reauthorise now to keep them going after that without a break.`,
    facts: [
      {
        key: 'plan',
        icon: <AutorenewRoundedIcon />,
        text: 'Your plan carries on and nothing changes',
      },
      {
        key: 'payment',
        icon: <CurrencyRupeeRoundedIcon />,
        text: (
          <>
            Next {payment} on <strong>{date}</strong>, as usual
          </>
        ),
      },
    ],
  };
};

/**
 * Asks a buyer whose Pro autopay was cancelled from their UPI or bank app
 * whether they meant it, on whichever page they land next.
 *
 * The backend decides when to ask (see `cancel_notice` on /subscription/mine);
 * this only renders it. It must be answered rather than dismissed: "Yes" stops
 * it for good, "No" re-sets the mandate from where it stopped.
 */
const SubscriptionCancelNotice = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { user, isLoginSuccess } = useAppSelector((state) => state.auth);
  const notice = useAppSelector(
    (state) => state.store.subscriptionCancelNotice,
  );
  const [isConfirming, setIsConfirming] = useState(false);
  const { restore, isRestoring, restoredId } = useSubscriptionRestore();

  // Not in the app's WebView, which cannot open Razorpay's sheet, and not in
  // Debug Mode, where an admin would be answering for the buyer.
  const eligible =
    isLoginSuccess &&
    user?.user_type === UserRole.Applicant &&
    !(window as any)?.ReactNativeWebView &&
    !isAdminMode();

  useEffect(() => {
    if (eligible) {
      dispatch(getMySubscription());
    }
  }, [eligible, user?.id, dispatch]);

  const open =
    eligible &&
    !!notice?.subscription_id &&
    notice.subscription_id !== restoredId &&
    !isRestoring &&
    !isDoNotDisturbPath(pathname);
  const isRazorpayReady = useRazorpay(open);

  if (!notice?.subscription_id) return null;
  const subscriptionId = notice.subscription_id;
  const copy = noticeCopy(notice);

  const confirm = async () => {
    setIsConfirming(true);
    await dispatch(confirmSubscriptionCancel(subscriptionId));
    setIsConfirming(false);
  };

  return (
    <NoticeDialog
      open={open}
      maxWidth="xs"
      aria-labelledby="subscription-cancel-notice-title"
      aria-describedby="subscription-cancel-notice-body"
    >
      <Shell>
        <Content>
          <Header>
            <GoldCoin>
              <WorkspacePremiumRoundedIcon />
              <CoinBadge>
                <SyncDisabledRoundedIcon />
              </CoinBadge>
            </GoldCoin>
            <ProChip sx={{ mt: 2.5 }}>Abekus Pro · Autopay</ProChip>
            <PitchTitle id="subscription-cancel-notice-title">
              {copy.title}
            </PitchTitle>
            <BodyText id="subscription-cancel-notice-body">
              {copy.body}
            </BodyText>
          </Header>
          <RestoreCard>
            <RestoreCardTitle>If it was a mistake</RestoreCardTitle>
            {copy.facts.map((fact) => (
              <FactRow key={fact.key}>
                <FactIcon>{fact.icon}</FactIcon>
                <FactText>{fact.text}</FactText>
              </FactRow>
            ))}
          </RestoreCard>
          {notice.authorization_amount ? (
            <FootNote>
              {`Your bank may show a ₹${notice.authorization_amount} verification charge, which Razorpay refunds automatically.`}
            </FootNote>
          ) : null}
          <Question>Did you mean to cancel it?</Question>
          <ActionsRow>
            <AnswerButton
              tone="paper"
              depth={4}
              onClick={confirm}
              disabled={isConfirming}
            >
              {isConfirming ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Yes, I cancelled it'
              )}
            </AnswerButton>
            <AnswerButton
              tone="gold"
              pulse
              onClick={() => restore(subscriptionId)}
              disabled={isConfirming || !isRazorpayReady}
            >
              No, it was a mistake
            </AnswerButton>
          </ActionsRow>
        </Content>
      </Shell>
    </NoticeDialog>
  );
};

export default SubscriptionCancelNotice;
